import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  AlertTriangle,
  BarChart3,
  TrendingUp,
  Clock,
  Users,
  Target,
  Network,
  ChevronDown,
  ChevronUp,
  Bell,
  LogOut,
  RefreshCw,
  Search,
  Layers,
  Activity,
  Copy,
  Eye,
  Calendar,
  User
} from 'lucide-react';
import IdeasPageHeader from './IdeasPageHeader';
import IdeaSelector from './IdeaSelector';


const BacklogCurator = ({ setActiveTab, selectedId, setSelectedIdeaId }) => {
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorStatus, setErrorStatus] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    similarIdeas: true,
    thematicClustering: true,
    historicalPerformance: true
  });

  const fetchAnalysisData = async (Id) => {
    setLoading(true);
    setErrorStatus(null);
    
    try {
      const endpoint = `/api/backlog/getbacklogcurator`;
      const params = new URLSearchParams({ Id });
      
      const response = await fetch(`${endpoint}?${params}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setAnalysisData(data);
    } catch (err) {
      setErrorStatus(500);
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedId) {
      fetchAnalysisData(selectedId);
    }
  }, [selectedId]);
  
  const getSimilarityColor = (percentage) => {
    if (percentage >= 80) return 'text-red-600';
    if (percentage >= 60) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getSimilarityBg = (percentage) => {
    if (percentage >= 80) return 'bg-red-50 border-red-200';
    if (percentage >= 60) return 'bg-yellow-50 border-yellow-200';
    return 'bg-green-50 border-green-200';
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'Approved': 'bg-green-100 text-green-800',
      'Pending': 'bg-orange-100 text-orange-800',
      'Under Review': 'bg-blue-100 text-blue-800',
      'In Review': 'bg-blue-100 text-blue-800',
      'Rejected': 'bg-red-100 text-red-800',
      'Implemented': 'bg-purple-100 text-purple-800',
      'ITPM Review': 'bg-orange-100 text-orange-800',
      'Unknown': 'bg-gray-100 text-gray-800'
    };
    
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleBackToIdeas = () => {
    setSelectedIdeaId(null);
    setActiveTab('ideas');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading backlog analysis...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!selectedId) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--cares-surface-board)' }}>
        <div style={{ padding: '24px' }}>
          {/* Professional Header with Breadcrumbs */}
          <IdeasPageHeader
            title="Backlog Curator"
            description="Intelligent backlog analysis and deduplication"
            breadcrumbPath="Backlog Curator"
          >
            <button
              onClick={handleBackToIdeas}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'linear-gradient(135deg, var(--cares-purple) 0%, var(--cares-purple) 100%)',
                color: 'var(--cares-text-max)',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
              }}
            >
              <ArrowLeft style={{ width: '18px', height: '18px' }} />
              <span>Back to Ideas</span>
            </button>
          </IdeasPageHeader>

          {/* Idea Selector */}
          <div style={{ marginBottom: '24px' }}>
            <IdeaSelector
              selectedId={selectedId}
              onSelectIdea={setSelectedIdeaId}
              placeholder="Select an idea to analyze..."
            />
          </div>

          {/* No Idea Selected Message */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            background: 'var(--cares-surface-card)',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <AlertTriangle style={{
                width: '48px',
                height: '48px',
                color: 'var(--cares-amber)',
                margin: '0 auto 16px'
              }} />
              <h2 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: 'var(--cares-text-1)',
                marginBottom: '8px'
              }}>
                No Idea Selected
              </h2>
              <p style={{
                fontSize: '14px',
                color: 'var(--cares-text-label)',
                marginBottom: '0'
              }}>
                Please select an idea from the dropdown above to view its backlog analysis and deduplication insights.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!analysisData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center">
            <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <p className="text-gray-600">No analysis data available for this idea.</p>
          </div>
        </div>
      </div>
    );
  }

  const displayData = analysisData ;

    const extractSponsor = (title) => {
    const match = title.match(/ - Sponsored by (.+)$/);
    return match ? match[1] : 'Unknown Sponsor';
  };

  const currentIdeaTitle = analysisData['Current Idea'];
  const sponsor = extractSponsor(currentIdeaTitle);
  const cleanTitle = currentIdeaTitle.replace(/ - Sponsored by .+$/, '');

  const formatDate = (dateString) => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      // If Date constructor fails, return original string
      return dateString;
    }
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  } catch {
    return dateString;
  }
};
 return (
    <div style={{ minHeight: '100vh', background: 'var(--cares-surface-board)' }}>
      <div style={{ padding: '24px' }}>
        {/* Professional Header with Breadcrumbs */}
        <IdeasPageHeader
          title="Backlog Curator"
          description={`Intelligent backlog analysis and deduplication for Idea ${selectedId}`}
          breadcrumbPath="Backlog Curator"
        >
          <button
            onClick={handleBackToIdeas}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'linear-gradient(135deg, var(--cares-purple) 0%, var(--cares-purple) 100%)',
              color: 'var(--cares-text-max)',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
            }}
          >
            <ArrowLeft style={{ width: '18px', height: '18px' }} />
            <span>Back to Ideas</span>
          </button>
          <button
            onClick={() => fetchAnalysisData(selectedId)}
            className="flex items-center space-x-2 bg-white text-gray-700 px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </IdeasPageHeader>

        {/* Idea Selector */}
        <div style={{ marginBottom: '24px' }}>
          <IdeaSelector
            selectedId={selectedId}
            onSelectIdea={setSelectedIdeaId}
            placeholder="Select an idea to analyze..."
          />
        </div>

        {/* Current Idea Analysis Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Current Idea Analysis</h2>
              <div className="flex items-center space-x-4 text-sm text-gray-600">
                <span className="flex items-center space-x-1">
                  <User className="w-4 h-4" />
                  <span>Submitted by {sponsor}</span>
                </span>
                <span className="flex items-center space-x-1">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(new Date().toISOString())}</span>
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className={`px-3 py-1 rounded-full text-sm font-medium inline-block ${getStatusBadge(analysisData.Status)}`}>
                {analysisData.Status}
              </div>
            </div>
          </div>
            
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                {cleanTitle}
              </h2>
              <p className="text-gray-700">
                {/* Add description if available from API */}
                {analysisData.description || 'No description available'}
              </p>
            </div>
        </div>

        {/* Main Content - Two Sections Horizontally */}
        <div className="grid grid-cols-2 gap-6">
          {/* Left Section - Similar Proposals */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Network className="w-5 h-5 mr-2" />
                  Similar Proposals
                </h3>
                <span className="text-sm text-gray-500">
                  Found {analysisData['Potential Duplicates Detected']} potentially similar ideas in the backlog
                </span>
              </div>
            </div>
            
            <div className="p-6">
              {analysisData['Similar Ideas'] && analysisData['Similar Ideas'].length > 0 ? (
                <>
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">
                        View Similar Proposals ({analysisData['Similar Ideas'].length})
                      </span>
                      <button className="text-blue-600 hover:text-blue-800 text-sm flex items-center">
                        <Eye className="w-4 h-4 mr-1" />
                        View All
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
          {analysisData['Similar Ideas'].map((idea, index) => (
            <div key={index} className={`p-4 rounded-lg border-2 ${getSimilarityBg(idea['Similarity %'])}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 mb-1">{idea['Title & Sponsor']}</h4>
                            <div className="flex items-center space-x-4 text-sm text-gray-600">
                <span>{extractSponsor(idea['Title & Sponsor'])}</span>
                <span>•</span>
                <span>{formatDate(idea['Submitted Date'])}</span>
              </div>
                          </div>
                          <div className="text-right ml-4">
                            <div className={`text-2xl font-bold ${getSimilarityColor(idea['Similarity %'])}`}>
                              {idea['Similarity %']}%
                            </div>
                            <div className="text-xs text-gray-500 mt-1">Similarity Match</div>
                          </div>
                        </div>
                        <div className="mt-2 flex items-center justify-between">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadge(idea.Status)}`}>
                            {idea.Status}
                          </span>
                          <button className="text-blue-600 hover:text-blue-800 text-sm">
                            {idea['ITPM Review Status'] || 'View Details'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No similar ideas found in the backlog</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Section - Thematic Clustering */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Layers className="w-5 h-5 mr-2" />
                Thematic Clustering
              </h3>
              <p className="text-sm text-gray-500 mt-1">Ideas grouped by common themes and topics</p>
            </div>

            <div className="p-6">
              {analysisData['Thematic Clustering'] ? (
                <div className="space-y-4">
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <h4 className="text-xl font-normal text-blue-900 flex-1 min-w-0 break-words">
                        {analysisData['Thematic Clustering']['Best Theme']}
                      </h4>
                      <span className="text-sm font-medium text-blue-900 whitespace-nowrap flex-shrink-0">
                        Match: {analysisData['Thematic Clustering']['Match %']}%
                      </span>
                    </div>
                    <div className="w-full bg-blue-200 rounded-full h-2 overflow-hidden" style={{ maxWidth: '100%' }}>
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${Math.min(100, Math.max(0, analysisData['Thematic Clustering']['Match %'] || 0))}%`,
                          maxWidth: '100%'
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No thematic clustering data available</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Section - Historical Performance */}
        <div className="mt-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Activity className="w-5 h-5 mr-2" />
                Historical Performance
              </h3>
              <p className="text-sm text-gray-500 mt-1">Success metrics for similar idea categories</p>
            </div>
            
            <div className="p-6">
              {analysisData['Historical Performance'] ? (
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-green-600 mb-2">
                      {analysisData['Historical Performance']['Success Rate']}
                    </div>
                    <div className="text-sm text-gray-600">Success Rate</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-blue-600 mb-2">
                      {analysisData['Historical Performance']['Average Implementation Time']}
                    </div>
                    <div className="text-sm text-gray-600">Avg. Implementation Time</div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No historical performance data available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default BacklogCurator;